import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { OpenStatementsService } from '../open-statements.service';
import { I18nModule, TranslationService } from '@spartacus/core';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormatNegativeValuePipe } from '../statement-display/format-negative-value.pipe';
import { combineLatest, first, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import { autoTable, ColumnInput, RowInput } from 'jspdf-autotable';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faFilePdf, faPrint } from '@fortawesome/free-solid-svg-icons';
import { AllStatements } from '../model/statment.model';
import { SpinnerModule } from '@spartacus/storefront';
import { CommonUtils } from '../../shared/utils/common.utils';
import { SalesOrg } from '../../shared/config/app.config';

@Component({
  selector: 'sanofi-statements-pdf-download',
  imports: [
    I18nModule,
    FaIconComponent,
    NgClass,
    SpinnerModule
  ],
  templateUrl: './statements-pdf-download.component.html'
})
export class StatementsPdfDownloadComponent implements OnInit {
  private readonly stService = inject(OpenStatementsService);
  private readonly translationService = inject(TranslationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly formatNegativePipe = inject(FormatNegativeValuePipe);

  accountNumber = input.required<string>();
  toDate = input.required<string>();
  isCustomerEmulated = input.required<boolean>();
  showSection = input<boolean>(true);
  generateAllBtn = input<boolean>(false);
  onModalView = input<boolean>(false);
  salesOrg = input.required<SalesOrg>();

  loading = signal(false);

  private accountNumberTrans: string;
  private statementDateTrans: string;
  private columnsHeaders: ColumnInput[];
  private sanofiNameTrans: string;
  private currencySymbolTrans: string;
  private billingAddressTrans: string;
  private analysisTrans: string;
  private currentAccountTrans: string;
  private thirtyDaysTrans: string;
  private sixtyDaysTrans: string;
  private ninetyDaysTrans: string;
  private statementsFileName: string;
  private addressFromTranslationFiles: string[];
  protected readonly faFilePdf = faFilePdf;
  private bothSalesOrgsAddressText: string;

  ngOnInit(): void {
    this.loading.set(false);
    this.getTranslations();
  }

  getAllStatementsAndGeneratePdf(): void {
    this.loading.set(true);
    const params = {
      accountId: this.accountNumber(),
      endDate: this.toDate(),
      fields: 'DEFAULT',
    };


    this.stService.getAllStatements(this.isCustomerEmulated() ? { emulatedUser: localStorage.getItem('asmUserId'), ...params } : params)
      .pipe(
        first(Boolean),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          const allStatementData: AllStatements = JSON.parse(JSON.stringify(res));
          allStatementData.statementTableData.sort((a, b) => CommonUtils.sortDateWithMoment(a.documentDate, b.documentDate));

          this.generateSavePdf(allStatementData);
          this.loading.set(false);
        },
        error: (error) => {
          console.info(error);
          this.loading.set(false);
        }
      });
  }

  private getTranslations(): void {
    combineLatest(([
      this.translationService.translate('accountOverview.statementsFileName'),
      this.translationService.translate('statementDisplay.account_number_label'),
      this.translationService.translate('statementDisplay.statement_date_label'),
      this.translationService.translate('statementDisplay.sanofi_name'),
      this.translationService.translate('statementDisplay.currency_symbol'),
      this.translationService.translate('statementDisplay.billing_address_label'),
      this.translationService.translate('statementDisplay.analysis_account_label'),
      this.translationService.translate('statementDisplay.current_account_label'),
      this.translationService.translate('statementDisplay.thirty_days_label'),
      this.translationService.translate('statementDisplay.sixty_days_label'),
      this.translationService.translate('statementDisplay.ninety_days_label'),
      this.getTranslationsForTopAddress(),
      this.getHeaders(),
      this.translationService.translate('statementDisplay.sanofiAddress.bothSalesOrgs')
    ]))
      .pipe(
        first(Boolean),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([
                    statementsFileName,
                    accountNumber,
                    statementDate,
                    sanofiName,
                    currencySymbol,
                    billingAddressLabel,
                    analysis,
                    currentAccount,
                    thirtyDays,
                    sixtyDays,
                    ninetyDays,
                    topAddress,
                    headersMap,
                    bothSalesOrgsAddressText
                  ]) => {
        this.statementsFileName = statementsFileName;
        this.addressFromTranslationFiles = topAddress;
        this.accountNumberTrans = accountNumber;
        this.statementDateTrans = statementDate;
        this.columnsHeaders = headersMap;
        this.sanofiNameTrans = sanofiName;
        this.currencySymbolTrans = currencySymbol;
        this.billingAddressTrans = billingAddressLabel;
        this.analysisTrans = analysis;
        this.currentAccountTrans = currentAccount;
        this.thirtyDaysTrans = thirtyDays;
        this.sixtyDaysTrans = sixtyDays;
        this.ninetyDaysTrans = ninetyDays;
        this.bothSalesOrgsAddressText = bothSalesOrgsAddressText;
      });
  }

  private generateSavePdf(allStatementData: AllStatements): void {
    const doc = new jsPDF();

    doc.addImage('img/Sanofi-Global-Header-logo.png', 'png', 15, 15, 100, 10);

    const isBothSalesOrgs = [SalesOrg.US12US03, SalesOrg.US03US12]
      .some(salesOrg => salesOrg.toLowerCase() === this.salesOrg()?.toLowerCase());

    doc.setFontSize(9);
    doc.text(isBothSalesOrgs ? this.bothSalesOrgsAddressText : this.addressFromTranslationFiles, 15, 32);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal', 'bold');
    doc.text(this.accountNumberTrans, 15, 65);
    doc.text(this.statementDateTrans, 100, 65);

    doc.setFont('helvetica', 'normal', 'normal');
    doc.text(this.accountNumber(), 75, 65);
    doc.text(this.datePipe.transform(this.toDate(), 'dd/MM/yyyy'), 155, 65);

    autoTable(doc, {
      bodyStyles: {
        lineWidth: {
          right: 0,
          bottom: 0.25,
          top: 0.25,
          left: 0,
        },
        lineColor: '#d3d6db',
        fillColor: '#e5e9ec'
      },
      columns: this.columnsHeaders,
      body: this.mapStatementDataForPdf(allStatementData),
      startY: 70,
      showHead: 'firstPage',
      styles: { overflow: 'linebreak', halign: 'center' },
      headStyles: {
        fillColor: '#23004c'
      },
      margin: { right: 15 },
      alternateRowStyles: {
        fillColor: '#fff'
      }
    });

    doc.setFont('helvetica', 'normal', 'bold');
    doc.text(this.sanofiNameTrans, 15, (doc as any).lastAutoTable.finalY + 5);

    doc.setFont('helvetica', 'normal', 'normal');
    doc.text(this.checkNegativeAndFormat(allStatementData.amountDueTotal, allStatementData.amountDueTotal),
      141,
      (doc as any).lastAutoTable.finalY + 5, { maxWidth: 25, align: 'center' });
    doc.text(this.currencySymbolTrans, 158, (doc as any).lastAutoTable.finalY + 5);

    const secondTableBody = [
      this.getAnalysisTableElement(this.currentAccountTrans, allStatementData?.currentPaymentAmountTotal),
      this.getAnalysisTableElement(this.thirtyDaysTrans, allStatementData?.last30DaysPaymentAmountTotal),
      this.getAnalysisTableElement(this.sixtyDaysTrans, allStatementData?.last60DaysPaymentAmountTotal),
      this.getAnalysisTableElement(this.ninetyDaysTrans, allStatementData?.last90DaysPaymentAmountTotal),
    ];

    autoTable(doc, {
      pageBreak: 'avoid',
      theme: 'plain',
      body: secondTableBody,
      startY: (doc as any).lastAutoTable.finalY + 25,
      showHead: false,
      styles: { overflow: 'linebreak', halign: 'left', fontSize: 12 },
      margin: { left: 115 },
      bodyStyles: {
        lineWidth: {
          right: 0,
          bottom: 0.25,
          top: 0.25,
          left: 0,
        },
        lineColor: '#d3d6db',
      },
      didDrawPage: (data) => {
        requestAnimationFrame(() => {
          const startTableY = data.settings.startY < data.table.finalY ? (data.settings.startY - 5) : 10;
          doc.setFont('helvetica', 'normal', 'bold');
          doc.setFontSize(10);
          doc.text(this.analysisTrans, data.settings.margin.left, startTableY);
          doc.text(this.billingAddressTrans, 15, startTableY);

          const soldToAddress = allStatementData?.soldToAddress;

          const billingAddressText = [
            soldToAddress.companyName ?? '',
            `${soldToAddress.line1?.toUpperCase() ?? ''} ${soldToAddress?.line2?.toUpperCase() ?? ''}`,
            `${soldToAddress.town ?? ''} ${soldToAddress.region?.isocodeShort?.toUpperCase() ?? ''} ${soldToAddress.postalCode?.toUpperCase() ?? ''}`,
          ];

          doc.setFont('helvetica', 'normal', 'normal');
          doc.setFontSize(12);
          doc.text(billingAddressText, 15, startTableY + 8, { maxWidth: 75 });

          doc.save(`${this.statementsFileName}.pdf`);
        })
      },
    });

    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    doc.addImage('img/vs-canada-sanofi-pasteur-logo.png', 'png', pageWidth - 43 - 15, (doc as any).lastAutoTable.finalY + 5, 43, 12);
  }

  private getHeaders(): Observable<ColumnInput[]> {
    return combineLatest([
      this.translationService.translate('statementDisplay.document_date_label'),
      this.translationService.translate('statementDisplay.due_date_label'),
      this.translationService.translate('statementDisplay.trans_type_label'),
      this.translationService.translate('statementDisplay.document_number_label'),
      this.translationService.translate('statementDisplay.original_amount_label'),
      this.translationService.translate('statementDisplay.amount_due_label'),
      this.translationService.translate('statementDisplay.currency_label'),
      this.translationService.translate('statementDisplay.terms_of_payment_label'),
    ])
      .pipe(
        map(([
               documentDateLabel,
               dueDateLabel,
               transTypeLabel,
               documentNumberLabel,
               originalAmountLabel,
               amountDueLabel,
               currencyLabel,
               termsOfPaymentLabel
             ]
        ) => {
          const headers: ColumnInput[] = [];

          headers.push({ dataKey: 'documentDate', header: documentDateLabel });
          headers.push({ dataKey: 'dueDate', header: dueDateLabel });
          headers.push({ dataKey: 'transType', header: transTypeLabel });
          headers.push({ dataKey: 'originalInvoice', header: documentNumberLabel });
          headers.push({ dataKey: 'originalAmount', header: originalAmountLabel });
          headers.push({ dataKey: 'amountDue', header: amountDueLabel });
          headers.push({ dataKey: 'currency', header: currencyLabel });
          headers.push({ dataKey: 'termsOfPayment', header: termsOfPaymentLabel });

          return headers;
        })
      );
  }

  private mapStatementDataForPdf(allStatementData: AllStatements): RowInput[] {
    if (!allStatementData?.statementTableData?.length) {
      return [];
    }

    return allStatementData?.statementTableData.map(data => {
      let mappedData: any = {
        documentDate: data.documentDate,
        dueDate: data.dueDate,
        transType: data.transType,
        originalInvoice: data.originalInvoice,
        originalAmount: this.checkNegativeAndFormat(data.originalAmount, data)
      };

      mappedData = {
        ...mappedData,
        amountDue: this.checkNegativeAndFormat(data.amountDue, data),
        currency: data.currency,
        termsOfPayment: data.termsOfPayment
      };

      return mappedData;

    });
  }

  private checkNegativeAndFormat(field: string | number, data: any, formatNegative = false) {
    let value: number | string = Number(field);

    if (formatNegative) {
      value = this.decimalPipe.transform(this.formatNegativePipe.transform(value), '1.2-2');

      return this.checkNegativeValue(field, formatNegative) ? `(${value})` : value;
    }

    value = this.decimalPipe.transform(field, '1.2-2');

    return this.checkNegativeValue(data, formatNegative) ? `(${value})` : value;
  }

  private getTranslationsForTopAddress(): Observable<string[]> {
    return combineLatest([
      this.translationService.translate(
        ['statementDisplay.sanofiAddress.line1', 'statementDisplay.sanofiAddress.bothSalesOrgs'],
        { context: this.salesOrg()?.toLowerCase() }
      ),
      this.translationService.translate(['statementDisplay.sanofiAddress.line2', 'statementDisplay.sanofiAddress.bothSalesOrgs'],
        { context: this.salesOrg()?.toLowerCase() }
      ),
      this.translationService.translate(['statementDisplay.sanofiAddress.line3', 'statementDisplay.sanofiAddress.bothSalesOrgs'],
        { context: this.salesOrg()?.toLowerCase() }
      ),
      this.translationService.translate(['statementDisplay.sanofiAddress.line4', 'statementDisplay.sanofiAddress.bothSalesOrgs'],
        { context: this.salesOrg()?.toLowerCase() }
      )
    ]);
  }

  private getAnalysisTableElement(trans: string, value: number): any[] {
    return [trans,
      this.currencySymbolTrans,
      this.checkNegativeAndFormat(value, value, true)
    ];
  }

  private checkNegativeValue(data, checkNegative = false) {
    return checkNegative ? (Number(data) < 0) : (data?.transType === 'Credit Memo');
  }

  protected readonly faPrint = faPrint;
}
